import {
    createStore,
    ObservableMap,
} from '@stencil/store';

import {
    AccountType,
    BuiltInGroups,
    STORAGE_KEYS,
} from '../../constants/constants';
import {
    defaultServiceOrder,
    defaultServiceOrderByPersona,
} from '../../constants/defaultServiceOrder';
import {
    Account,
    AccountAndTenantsStoreState,
} from '../../models/account';
import { LEFT_NAV_PERSONAS } from '../../models/persona';
import { Service } from '../../models/service';
import { defaultActiveItems } from '../../store/defaultState';
import store from '../../store/store';
import { getCurrentUserPersona } from '../api/personaApi';
import {
    getAccount,
    getSessionSettings,
} from '../api/tenantsAndOrganizationInfoApi';
import { AppInsightsInstanceResolver } from '../appInsights/appInsightsInstanceResolver';
import {
    isMsi,
    isMsiOrStandalone,
    isNonCloud,
} from '../EnvironmentUtil';
import { EventHandlersInstanceResolver } from '../events/eventHandlersInstanceResolver';
import GlobalEventEmitter from '../events/globalEventEmitters';
import { Features } from '../featureFlags/featureFlagConstants';
import {
    getFeatureFlag,
    getFeatureFlags,
} from '../featureFlags/featureFlags';
import { GlobalPropsInstanceResolver } from '../globalProps/globalPropsInstanceResolver';
import {
    caseInsensitiveCompare,
    isFalsyOrWhitespace,
} from '../helper';
import { getLanguage } from '../languageHelper';
import Logger from '../logger/logger';
import { getMenuConfig } from '../menu';
import { getOrganizationStandardUrl } from '../orgStandardUrlHelper';
import {
    getAccessToken,
    sessionStore,
} from '../session/sessionStore';
import {
    getAccountName,
    getTenantName,
} from '../urlHelper';

export default class AccountAndTenants {
    private static accountCacheKey = STORAGE_KEYS.PORTAL_ACCOUNT;
    private static tenantCacheKey = STORAGE_KEYS.PORTAL_CURRENT_TENANT;
    private static personaStorageKey = STORAGE_KEYS.PORTAL_SHELL_PERSONA;
    private static personaLastRefetchedKey = STORAGE_KEYS.PORTAL_SHELL_PERSONA_LAST_REFETCHED;
    private static accountAndTenantsLastRefetchedKey = STORAGE_KEYS.PORTAL_SHELL_ACCOUNT_AND_TENANTS_LAST_REFETCHED;

    private accountAndTenantsStore: ObservableMap<AccountAndTenantsStoreState>;

    constructor(accountStore: ObservableMap<AccountAndTenantsStoreState>) {
        this.accountAndTenantsStore = accountStore;

        this.processAccount();
        this.getPersona(getAccessToken());
    }

    /**
     * Call this method to retrigger the /tenantsAndOrganizationInfo call
     * and update all the components with the new values
     *
     * Corresponds to the original RefreshAccountCache implemented in the old shell
     *
     * @param accessToken (optional) The access token to make the call with.
     * @param isHost (optional) Whether or not this is a host account, which is a field in the decoded token
     */
    async fetchAccountAndTenants(
        forceRefetch = false,
        accessToken = getAccessToken(),
        isHost = sessionStore.get('isHost'),
    ) {
        Logger.debug('accountAndTenants', 'fetching account and tenants');
        if (await this.handleOnPremApiCall(accessToken, isHost)) {
            this.updateMenuItems();
            return;
        }

        const enablePortalShellV3 = getFeatureFlag(Features.EnablePortalShellV3.name);
        const refetchThrottleMs = +getFeatureFlag<number>(Features.AccountAndTenantsRefetchThrottleMs.name);
        const currentTime = new Date().getTime();
        const lastRefetch = +(localStorage.getItem(AccountAndTenants.accountAndTenantsLastRefetchedKey) ?? 0);
        const shouldFetch = !enablePortalShellV3
            || forceRefetch
            || !this.accountAndTenantsStore.get('organization')
            || (currentTime - lastRefetch) > refetchThrottleMs;

        const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
        if (shouldFetch && organizationStandardUrl && accessToken) {
            const account = await getAccount(organizationStandardUrl, accessToken);
            Logger.info('api', 'account fetch success', { object: account });

            localStorage.setItem(AccountAndTenants.accountAndTenantsLastRefetchedKey, currentTime.toString());

            this.processAccount(account, accessToken);
        }

        await this.getPersona(accessToken);
        this.updateMenuItems();
    }

    /**
     * INTERNAL USE ONLY
     * Gets the menu items for the current account, tenant and persona.
     * Pass in the value that caused the menu items to update
     *
     * @internal
     * @returns the menu items as an array of Service objects
     */
    updateMenuItems({
        tenantName = this.accountAndTenantsStore.get('currentTenant'),
        isHost = sessionStore.get('isHost'),
        locale = getLanguage(store),
        persona = this.accountAndTenantsStore.get('persona'),
        overrideServices = this.accountAndTenantsStore.get('overrideServices'),
    }: {
        tenantName?: string;
        isHost?: boolean;
        locale?: string;
        persona?: string;
        overrideServices?: string[];
    } = {}) {
        if (overrideServices.length > 0) {
            this.accountAndTenantsStore.set('overrideServices', overrideServices);
        }

        const menuConfig = getMenuConfig({
            tenantName,
            isHost,
            locale,
        });
        const moreMenuItems: Service[] = [];

        if (isHost) {
            Logger.info('accountAndTenants', 'returning menu items for host');
            this.accountAndTenantsStore.set('menuItems', menuConfig.services.filter(service => !!service.showInHostAccount));
            return;
        }

        Logger.debug('accountAndTenants', 'returning menu items for cloud');

        const isLeftNavServiceRevampEnabled = getFeatureFlag(Features.EnableLeftNavServiceRevamp.name);
        let serviceOrderByPersona;
        if (isLeftNavServiceRevampEnabled) {
            serviceOrderByPersona = defaultServiceOrderByPersona(getFeatureFlags, Features);
        }

        const coreServiceOrder: string[] = isLeftNavServiceRevampEnabled
            ? [ ...serviceOrderByPersona[persona].core ]
            : [ ...defaultServiceOrder ];
        const moreServiceOrder: string[] = isLeftNavServiceRevampEnabled
            ? [ ...serviceOrderByPersona[persona].more ]
            : [];

        Logger.debug('store', 'core service order', { object: coreServiceOrder });
        Logger.debug('store', 'more service order', { object: moreServiceOrder });

        const accountServices = defaultActiveItems.concat(this.accountAndTenantsStore.state.organization?.accountServices ?? []);
        Logger.debug('store', 'account level services', { object: accountServices });

        const tenant = this.getTenant(this.accountAndTenantsStore.get('currentTenant')) ?? this.accountAndTenantsStore.get('tenants')[0];
        Logger.debug('store', 'used for menu items', { object: tenant });

        const enabledServices = tenant?.enabledServices ?? [];
        const activeItems = (isMsiOrStandalone()) ? overrideServices : enabledServices.concat(accountServices);
        const coreActiveServices = coreServiceOrder.filter(service => activeItems.includes(service));
        const moreActiveServices = moreServiceOrder.filter(service => activeItems.includes(service));

        const menuItems: Service[] = [];
        coreActiveServices.forEach((activeService) => {
            const service = menuConfig.services.find(menuConfigService => caseInsensitiveCompare(menuConfigService.id, activeService));
            if (service) {
                menuItems.push(service);
            }
        });

        moreActiveServices.forEach((activeService) => {
            const service = menuConfig.services.find(menuConfigService => caseInsensitiveCompare(menuConfigService.id, activeService));
            if (service) {
                moreMenuItems.push(service);
            }
        });

        this.accountAndTenantsStore.set('menuItems', [
            ...menuItems,
            ...moreMenuItems.map(service => ({
                ...service,
                isMore: true,
            })),
        ]);
    }

    /**
     * Get the current account and tenant info
     * @returns the tenant list as an array of Tenant objects
     */
    getCurrentAccountAndTenantsInfo(): Account {
        return {
            organization: {
                name: getAccountName() ?? '',
                ...this.accountAndTenantsStore.get('organization'),
            },
            tenants: [ ...this.accountAndTenantsStore.get('tenants') ],
            userGroups: [ ...this.accountAndTenantsStore.get('userGroups') ],
        };
    }

    /**
     * Gets the tenant of the current account based on the passed in tenant name or id,
     * or, if none is passed in, the tenant passed to the global properties, the tenant in the
     * current url, or the cached tenant in that priority
     *
     * @param tenantNameOrId
     * @returns the Tenant object
     */
    getTenant(
        tenantNameOrId: string = GlobalPropsInstanceResolver.Instance?.devProperties?.tenant
            ?? getTenantName()
            ?? this.getCachedTenantName(),
    ) {
        const tenants = this.accountAndTenantsStore.get('tenants');
        if (tenants?.length < 1) {
            return;
        }

        return tenants.find(
            tenant => caseInsensitiveCompare(tenant.name, tenantNameOrId)
                || caseInsensitiveCompare(tenant.id, tenantNameOrId),
        );
    }

    /**
     * INTERNAL USE ONLY
     * Updates the current tenant of the shell to the passed-in tenant name or id
     *
     * @internal
     * @param tenantNameOrId
     */
    updateCurrentTenant(tenantNameOrId?: string, forceRefresh = false) {
        Logger.debug([ 'accountAndTenants', 'tenant' ], 'updating current tenant', { object: tenantNameOrId });
        const storeTenantName = this.accountAndTenantsStore.get('currentTenant');
        const currentTenant = this.getTenant(tenantNameOrId) ?? this.accountAndTenantsStore.get('tenants')[0];
        if (!forceRefresh && (!currentTenant || (storeTenantName && caseInsensitiveCompare(currentTenant.name, storeTenantName)))) {
            Logger.debug([ 'accountAndTenants', 'tenant' ], 'no tenant change', {
                object: {
                    storeTenantName,
                    currentTenant,
                },
            });
            return;
        }

        const tenantEvent = {
            id: currentTenant.id,
            name: currentTenant.name,
            selectedTenantId: currentTenant.name,
        };

        if (!this.accountAndTenantsStore.get('currentTenant')) {
            GlobalEventEmitter.emitTenantLoaded(tenantEvent);
            EventHandlersInstanceResolver.Instance?.onTenantLoaded?.(tenantEvent);
        }

        this.accountAndTenantsStore.set('currentTenant', currentTenant.name);
        const defaultEmitter = store.get('tenantChangedEmitter');
        GlobalEventEmitter.emitTenantChanged(tenantEvent,
            defaultEmitter ? () => defaultEmitter.emit(tenantEvent) : undefined,
        );
        EventHandlersInstanceResolver.Instance?.onTenantChanged?.(tenantEvent);

        this.updateMenuItems({ tenantName: currentTenant.name });

        AccountAndTenants.setCachedTenant(currentTenant.name);

        AppInsightsInstanceResolver.Instance.setPlatformInfo({
            cloudTenantId: currentTenant.id,
            tenantId: currentTenant.id,
        });
    }

    /**
     * Returns true if the user is an admin
     *
     * @returns true if the user is an admin
     */
    isAdminUserGroup() {
        return this.accountAndTenantsStore?.get('userGroups').includes(BuiltInGroups.Admin) ?? false;
    }

    /**
     * Returns true if the resource center is hidden for the org
     *
     * @returns true if the resource center is hidden for the org
     */
    isResourceCenterHiddenForNonAdmins() {
        return this.accountAndTenantsStore.get('organization')?.resourceCenterHidden ?? false;
    }

    private static setCachedAccount(account: Account | undefined) {
        if (!account) {
            return;
        }
        localStorage.setItem(AccountAndTenants.accountCacheKey, JSON.stringify(account));
    }

    private static setCachedTenant(tenantNameOrId: string) {
        localStorage.setItem(AccountAndTenants.tenantCacheKey, tenantNameOrId);
    }

    private static setCachedPersona(persona: string) {
        localStorage.setItem(AccountAndTenants.personaStorageKey, persona);
    }

    private getCachedAccount(): Account | undefined {

        // Disabling the rule here to simplify because we don't want to consider empty strings either
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        const accountNameOrId = this.accountAndTenantsStore.get('organization')?.name
            || this.accountAndTenantsStore.get('organization')?.id
            || sessionStore.get('organizationId')
            || getAccountName();
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

        if (!accountNameOrId) {
            return;
        }

        const cachedAccountString = localStorage.getItem(AccountAndTenants.accountCacheKey);
        try {
            const cachedAccount = JSON.parse(cachedAccountString ?? '') as Account;
            const isMatch = caseInsensitiveCompare(cachedAccount?.organization?.name ?? '', accountNameOrId)
                || caseInsensitiveCompare(cachedAccount?.organization?.id ?? '', accountNameOrId);
            if (isMatch) {
                return cachedAccount;
            }
            Logger.warn('cache', `failed to retrieve cache; accountNameOrId: ${accountNameOrId}, cachedAccountName: ${cachedAccount?.organization?.name}, cachedAccountId: ${cachedAccount?.organization?.id}, caseInsensitiveMatch: ${isMatch}`);
            return;
        } catch {
            return;
        }
    }

    private getCachedTenantName() {
        let tenantNameOrId = localStorage.getItem(AccountAndTenants.tenantCacheKey) ?? '';
        if (isFalsyOrWhitespace(tenantNameOrId)) {
            tenantNameOrId = getTenantName() ?? '';
        }

        return tenantNameOrId;
    }

    private getCachedPersona() {
        return localStorage.getItem(AccountAndTenants.personaStorageKey) ?? LEFT_NAV_PERSONAS.INITIAL;
    }

    private async handleOnPremApiCall(accessToken: string, isHost: boolean) {
        const isMSI = isMsi();
        const isK8sHost = isHost && !isMSI;

        if (!isMsiOrStandalone() && !isK8sHost) {
            return false;
        }

        const fallBackTenantList = [
            {
                name: this.accountAndTenantsStore.get('currentTenant'),
                id: '',
                status: 'Enabled',
                enabledServices: [], // store.get('overrideServices')
                color: '',
                isCanaryTenant: false,
            },
        ];

        if (
            (isMSI || isK8sHost) && (
                getFeatureFlag(Features.EnablePortalShellIdleTimeout.name)
                        || getFeatureFlag(Features.EnablePortalShellSingleSession.name)
            ) && accessToken
        ) {
            const sessionSettings = await getSessionSettings(accessToken, isMSI);
            Logger.info([ 'api', 'msi' ], 'session settings fetch success', { object: sessionSettings });

            this.accountAndTenantsStore.set('organization', {
                ...this.accountAndTenantsStore.state.organization ?? { name: isK8sHost ? 'host' : 'msi' },
                ...sessionSettings,
            });
            this.accountAndTenantsStore.set('tenants', isK8sHost ? [] : fallBackTenantList);
            this.accountAndTenantsStore.set('userGroups', []);

            this.updateIdleSessionTimeout(sessionSettings.idleSessionTimeoutPeriod);
        } else { // Standalone case or session flags are not enabled
            this.accountAndTenantsStore.set('tenants', fallBackTenantList);
            this.accountAndTenantsStore.set('currentTenant', fallBackTenantList[0].name);
        }
        return true;
    }

    private processAccount(account = this.getCachedAccount(), accessToken?: string) {
        // Update the store
        this.accountAndTenantsStore.set('organization', account?.organization);
        this.accountAndTenantsStore.set('tenants', account?.tenants ?? []);
        this.accountAndTenantsStore.set('userGroups', account?.userGroups ?? []);

        if (!account) {
            return;
        }

        if (this.accountAndTenantsStore.get('tenants').length < 1) {
            const defaultEmitter = store.get('tenantsNotAvailableEmitter');
            GlobalEventEmitter.emitTenantsNotAvailable(
                defaultEmitter ? () => defaultEmitter.emit() : undefined,
            );
            EventHandlersInstanceResolver.Instance?.onTenantsNotAvailable?.();
            this.accountAndTenantsStore.set('currentTenant', '');
        } else {
            this.updateCurrentTenant(undefined, true);
        }

        // Handle the case when the idleSessionTimeout updated
        this.updateIdleSessionTimeout(account?.organization?.idleSessionTimeoutPeriod);

        // Handle the case for Pendo
        if (account && accessToken && !isNonCloud()) {
            this.updateOrInitializePendo();
        }

        AccountAndTenants.setCachedAccount(account);
    }

    private updateIdleSessionTimeout(idleSessionTimeoutPeriod?: number) {
        if (!idleSessionTimeoutPeriod) {
            window.PortalShell?.Session.cancelIdleTimeout();
        } else if (sessionStore.get('isSignInSuccessful') && getFeatureFlag(Features.EnablePortalShellIdleTimeout.name)) {
            Logger.debug('idle timeout', 'starting idle timeout from account side effect');
            window.PortalShell?.Session.enforceIdleTimeout(
                idleSessionTimeoutPeriod,
            );
        }
    }

    private updateOrInitializePendo() {
        Logger.debug([ 'accountAndTenants', 'pendo' ], 'attempt to update all options');
        window.PortalShell?.Pendo.updateOptionsFromStore();

        Logger.debug([ 'accountAndTenants', 'pendo' ], 'attempt init');
        window.PortalShell?.Pendo.initializeFromStore();
    }

    private async getPersona(accessToken?: string) {
        const isLeftNavServiceRevampEnabled = getFeatureFlag(Features.EnableLeftNavServiceRevamp.name);

        if (isLeftNavServiceRevampEnabled && this.accountAndTenantsStore.get('organization')) {
            let persona = this.getCachedPersona();
            const isAdmin = this.accountAndTenantsStore.get('userGroups').includes(BuiltInGroups.Admin);
            const isAutomationExpress = this.accountAndTenantsStore.get('organization')?.accountType?.toUpperCase() === AccountType.Free;

            const refetchThrottleMs = +getFeatureFlag<number>(Features.PersonaRefetchThrottleMs.name);
            const currentTime = new Date().getTime();
            const lastRefetch = +(localStorage.getItem(AccountAndTenants.personaLastRefetchedKey) ?? 0);

            const refetchCondition = this.accountAndTenantsStore.get('persona') === LEFT_NAV_PERSONAS.INITIAL ||
                (currentTime - lastRefetch > refetchThrottleMs);

            let apiResolved = false;

            if (isAutomationExpress && isAdmin) {
                persona = LEFT_NAV_PERSONAS.AUTOMATION_EXPRESS_ADMIN;
            } else if (isAutomationExpress) {
                persona = LEFT_NAV_PERSONAS.AUTOMATION_EXPRESS_USER;
            } else if (isAdmin) {
                persona = LEFT_NAV_PERSONAS.ADMIN;
            } else if (refetchCondition && accessToken) {
                persona = await getCurrentUserPersona(accessToken);
                apiResolved = true;

                localStorage.setItem(AccountAndTenants.personaLastRefetchedKey, currentTime.toString());
            }

            // The other cased is logged within `getCurrentUserPersona` method.
            if (!apiResolved) {
                Logger.info('persona', 'resolved', {
                    object: {
                        dominantPersona: persona,
                        fromApiCall: false,
                    },
                });
            }

            this.accountAndTenantsStore.set('persona', persona);
            persona !== LEFT_NAV_PERSONAS.INITIAL && AccountAndTenants.setCachedPersona(persona);
        }
    }
}

export const accountAndTenantsStore = createStore<AccountAndTenantsStoreState>({
    organization: undefined,
    tenants: [],
    userGroups: [],
    currentTenant: '',
    persona: LEFT_NAV_PERSONAS.INITIAL,
    menuItems: [],
    overrideServices: [],
});
